// dashboard/frontend/src/components/Profile/Profile.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Grid,
    IconButton,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../../api/axios';
import moment from 'moment-timezone';

const Profile = () => {
    // Add loading state
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        profile: {
            company_name: '',
            job_title: '',
            send_from_name: '',
            send_from_email: '',
            email_signature: '',
            writing_samples: [],
            timezone: ''
        }
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [newSample, setNewSample] = useState('');
    const timezones = moment.tz.names().filter(tz => 
        tz.includes('America') || 
        tz.includes('Europe') || 
        tz.includes('Asia/Dubai') ||
        tz.includes('Asia/Singapore') ||
        tz.includes('Australia')
    );

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            console.log('Fetching profile...');
            const response = await api.get('/auth/profile/');
            console.log('Profile response:', response.data);
            
            // Update the user data structure
            setUserData({
                first_name: response.data.user.first_name || '',
                last_name: response.data.user.last_name || '',
                email: response.data.user.email || '',
                profile: {
                    company_name: response.data.user.profile.company_name || '',
                    job_title: response.data.user.profile.job_title || '',
                    send_from_name: response.data.user.profile.send_from_name || '',
                    send_from_email: response.data.user.profile.send_from_email || '',
                    email_signature: response.data.user.profile.email_signature || '',
                    writing_samples: response.data.user.profile.writing_samples || [],
                    timezone: response.data.user.profile.timezone || ''
                }
            });
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('profile.')) {
            const profileField = name.split('.')[1];
            setUserData(prev => ({
                ...prev,
                profile: {
                    ...prev.profile,
                    [profileField]: value
                }
            }));
        } else {
            setUserData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAddSample = () => {
        if (newSample.trim()) {
            setUserData(prev => ({
                ...prev,
                profile: {
                    ...prev.profile,
                    writing_samples: [...prev.profile.writing_samples, newSample]
                }
            }));
            setNewSample('');
        }
    };

    const handleDeleteSample = (index) => {
        setUserData(prev => ({
            ...prev,
            profile: {
                ...prev.profile,
                writing_samples: prev.profile.writing_samples.filter((_, i) => i !== index)
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            console.log('Submitting profile update...');
            
            // Split the data into user data and profile data
            const updateData = {
                user: {
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    email: userData.email
                },
                profile: {
                    company_name: userData.profile.company_name,
                    job_title: userData.profile.job_title,
                    send_from_name: userData.profile.send_from_name,
                    send_from_email: userData.profile.send_from_email,
                    email_signature: userData.profile.email_signature,
                    writing_samples: userData.profile.writing_samples,
                    timezone: userData.profile.timezone
                }
            };

            console.log('Update data:', updateData);

            // Use PUT instead of PATCH
            const response = await api.put('/auth/profile/', updateData);

            console.log('Update response:', response.data);
            setSuccess('Profile updated successfully');
            
            // Refresh the profile data
            await fetchProfile();
        } catch (err) {
            console.error('Update error:', err);
            setError(err.response?.data?.error || 'Failed to update profile');
        }
    };

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
                <Typography variant="h4" gutterBottom>
                    Profile Settings
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="first_name"
                                value={userData.first_name}
                                onChange={handleChange}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="last_name"
                                value={userData.last_name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={userData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                    value={userData.profile.timezone || ''}
                                    onChange={(e) => handleChange({
                                        target: {
                                            name: 'profile.timezone',
                                            value: e.target.value
                                        }
                                    })}
                                    label="Timezone"
                                >
                                    {timezones.map((tz) => (
                                        <MenuItem key={tz} value={tz}>
                                            {tz} ({moment.tz(tz).format('Z')})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Company Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Company Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                name="profile.company_name"
                                value={userData.profile.company_name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Job Title"
                                name="profile.job_title"
                                value={userData.profile.job_title}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* Email Settings */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Email Settings
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Send From Name"
                                name="profile.send_from_name"
                                value={userData.profile.send_from_name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Send From Email"
                                name="profile.send_from_email"
                                type="email"
                                value={userData.profile.send_from_email}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email Signature"
                                name="profile.email_signature"
                                multiline
                                rows={4}
                                value={userData.profile.email_signature}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* Writing Samples */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Writing Samples
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Add samples of your writing style to help customize AI-generated content
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="New Writing Sample"
                                    value={newSample}
                                    onChange={(e) => setNewSample(e.target.value)}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddSample}
                                    sx={{ mt: 1 }}
                                >
                                    Add Sample
                                </Button>
                            </Box>

                            {userData.profile.writing_samples.map((sample, index) => (
                                <Box key={index} sx={{ mb: 2, position: 'relative' }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={sample}
                                        disabled
                                    />
                                    <IconButton
                                        onClick={() => handleDeleteSample(index)}
                                        sx={{ position: 'absolute', top: 0, right: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Grid>
                    </Grid>

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{ mt: 4 }}
                    >
                        Save Changes
                    </Button>
                </form>
            </Paper>
        </Container>
    );
};

export default Profile;